import { FC, useCallback, useState } from "react";
import { Tooltip } from "rizzui";
import { toast } from "sonner";
import DeletePopover from "../../../../../components/buttons/DeletePopover";
import { FormDrawerView } from "../../../../../components/forms/FormDrawer";
import { useFiltersPR } from "../../../../../components/providers/FiltersProvider";
import { FilterDrawerView } from "../../../../../components/table/components/TableFilter";
import CustomTable from "../../../../../components/table/CustomTable";
import useFetch from "../../../../../hooks/useFetch";
import useHandleErrors from "../../../../../hooks/useHandleErrors";
import Page from "../../../../../layout/Page/Page";
import { StockMovementService } from "../../../../../services/stockMovements/stockMovementService";
import { StockMovement, StockMovementApiResponse } from "../../../../../type/entities/stock-movement-type";
import { STOCK_MOVEMENT_TYPE } from "../../../../../utils/mapping";
import ProductStockMovementsFilters from "./ProductStockMovementsFilters";
import { useParams } from "react-router-dom";
import { PiUser } from "react-icons/pi";

interface ProductStockMovementsListProps {
}

const columnOrder = [
    'id',
    'product',
    'type',
    'quantity',
    'totalPrice',
    'source',
    'createdBy',
    'createdAt',
]

const ProductStockMovements: FC<ProductStockMovementsListProps> = ({}) => {

    // STATES
    const { id = '' } = useParams<{ id: string }>();
    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const service = new StockMovementService();

    // HOOKS

    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();
    const { handleErrors } = useHandleErrors();

    // FETCH DATA

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTIENE EL LISTADO DE MOVIMIENTOS DE STOCK
     * @EN GETS THE LIST OF STOCK MOVEMENTS
     */
    // -------------------------------------------------------------------------------------------------------------
    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        if (!id) return;
        const params = {
            ...filters,
            filter_filters: {
                ...filters.filter_filters,
                product: id,
            }
        }
        const response = await service.listStockMovements(params);
        return response.getResponseData() as StockMovementApiResponse;
    }, [filters]));
    // -------------------------------------------------------------------------------------------------------------

    // FUNCTIONS

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MUESTRA LOS DATOS EN EL ORDEN CORRECTO
     * @EN SHOWS THE DATA IN THE CORRECT ORDER
     */
    //-------------------------------------------------------------------------------------------------------------
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINAR MULTIPLES MOVIMIENTOS DE STOCK
     * @EN DELETES MULTIPLE STOCK MOVEMENTS
     * 
     * @param ids - ARRAY DE ID'S DE LOS MOVIMIENTOS DE STOCK A ELIMINAR
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleMultipleDelete = async (ids: string[]) => {
        try {
            const response = (await service.deleteMultiStockMovement(ids)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Movimientos de stock eliminados correctamente');
            } else {
                handleErrors(response);

            }
        } catch (error) {
            handleErrors(error);
        }
    }
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJA LA ELIMINACION DE UN MOVIMIENTO DE STOCK
     * @EN HANDLES THE DELETION OF A STOCK MOVEMENT
     * 
     * @param id - ID DEL MOVIMIENTO DE STOCK A ELIMINAR
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        try {
            const response = (await service.deleteStockMovement(id)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Movimiento de stock eliminado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    }
    //-------------------------------------------------------------------------------------------------------------

    // RENDER

    return (
        <Page container='fluid'>
            {data !== undefined && (
                <CustomTable
                    id={'stock-movement-table'}
                    columnOrder={columnOrder}
                    data={orderedData()}
                    isLoading={loading}
                    isSortable
                    columnsNoSortables={[]}
                    columnsNotShown={[
                    ]}
                    overrideColumns={[
                        {
                            key: 'company',
                            columnNameKey: 'Empresa',
                            permissions: [{
                                group: 'company',
                                permission: 'list'
                            }],
                            render: (row: any) => {
                                if (!row.row.original?.company) return 'N/A';
                                return (
                                    <div>
                                        {row.row.original?.company?.name}
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'type',
                            columnNameKey: 'Tipo',
                            render: (row: any) => {
                                return STOCK_MOVEMENT_TYPE?.find((g: any) => g.value === row.row.original?.type)?.label
                            }
                        },
                        {
                            key: 'product',
                            columnNameKey: 'Producto',
                            render: (row: any) => {
                                if (!row.row.original?.product) return 'N/A';
                                return (
                                    <div>
                                        {row.row.original?.product?.name}
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'source',
                            columnNameKey: 'Origen/Destino',
                            render: (row: any) => {
                                if (!row.row.original?.source) return 'N/A';
                                return (
                                    <div>
                                        {row.row.original?.source}
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'totalPrice',
                            columnNameKey: 'Precio Total',
                            render: (row: any) => {
                                const totalPrice = row.row.original.totalPrice ? row.row.original.totalPrice.toFixed(2) : "N/A";
                                return `${totalPrice} €`;
                            }
                        },
                        {
                            key: 'createdBy',
                            columnNameKey: 'Creado por',
                            render: (row: any) => {
                                const user = row.row.original?.createdBy;
                                const userName = user ? `${user.name} ${user.lastName || ''}`.trim() : null;
                                return (
                                    <div className="flex flex-col gap-1">
                                        <div className="font-bold flex items-center gap-2 text-sm text-gray-900">
                                            <PiUser className="text-gray-400 min-h-[25px] min-w-[25px]" />
                                            <span>{userName || 'Sin asignar'}</span>
                                        </div>
                                        <div className="text-sm text-gray-500 text-nowrap">
                                            <span className="font-bold">DNI: </span>
                                            {user?.dni ?? 'N/A'}
                                        </div>
                                        <div className="text-sm text-gray-500 text-nowrap">
                                            <span className="font-bold">Email: </span>
                                            {user?.email ?? 'N/A'}
                                        </div>
                                    </div>
                                );
                            }
                        }
                    ]}
                    actions={[
                        {
                            label: 'Delete',
                            permissions: { group: 'stock_movements', permission: 'delete' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Eliminar'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <div>
                                            <DeletePopover
                                                title={`Eliminar movimiento de stock`}
                                                description={`¿Estás seguro de que deseas eliminar el movimiento de stock?`}
                                                onDelete={() => handleDelete(row.row.original?.id)}
                                            />
                                        </div>
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: (data as StockMovementApiResponse) ? data.lastPage : 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}
                    handleMultipleDelete={handleMultipleDelete}
                />
            )}

            <FilterDrawerView
                isOpen={openFilters}
                setOpenDrawer={setOpenFilters}
                drawerTitle={'Filtrar movimientos de stock'}
                onClear={() => resetFilters(50)}
            >
                <ProductStockMovementsFilters
                    filters={filters}
                    updateFilters={updateFilters}
                />
            </FilterDrawerView>

        </Page>

    )
}

export default ProductStockMovements;
